/**
 * HospitalOrdersPanel
 * --------------------
 * Read-only "My Orders" view for the hospital portal. Mirrors the data the
 * Admin Orders Command Center shows but trims internal-only fields (admin
 * notes, internal communications). Hospitals see:
 *   • Order ID, items, totals (incl. freight choice)
 * • Status, tracking #, carrier, expected delivery
 *   • Their own customer notes
 *   • Reverse-chronological status timeline + outbound comms from Sterile Mate
 */

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShoppingCart, RefreshCw, Loader2, Truck, Package, MapPin, Clock,
  CheckCircle2, AlertCircle, Mail, ExternalLink, X, ClipboardList,
  Edit3, Trash2, BellRing, Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

import { API } from "@/App";
import HospitalBackorderTimeline from "@/components/HospitalBackorderTimeline";

const STATUS_META = {
  new:             { label: "New",            cls: "bg-blue-100 text-blue-800",        icon: AlertCircle },
  quoted:          { label: "Quoted",         cls: "bg-amber-100 text-amber-800",      icon: ClipboardList },
  po_received:     { label: "PO Received",    cls: "bg-violet-100 text-violet-800",    icon: CheckCircle2 },
  in_fulfillment:  { label: "In Fulfillment", cls: "bg-cyan-100 text-cyan-800",        icon: Package },
  shipped:         { label: "Shipped",        cls: "bg-emerald-100 text-emerald-800",  icon: Truck },
  delivered:       { label: "Delivered",      cls: "bg-slate-200 text-slate-700",      icon: CheckCircle2 },
  on_hold:         { label: "On Hold",        cls: "bg-yellow-100 text-yellow-800",    icon: Clock },
  cancelled:       { label: "Cancelled",      cls: "bg-rose-100 text-rose-800",        icon: X },
};

const TRACK_URL = (carrier, tracking) => {
  if (!tracking) return null;
  const c = (carrier || "").toLowerCase();
  if (c.includes("fedex")) return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tracking)}`;
  if (c.includes("ups"))   return `https://www.ups.com/track?tracknum=${encodeURIComponent(tracking)}`;
  if (c.includes("usps"))  return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(tracking)}`;
  if (c.includes("dhl"))   return `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(tracking)}`;
  return null;
};

const fmt$ = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtAge = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const fmtTs = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status || "—", cls: "bg-slate-100 text-slate-600", icon: Clock };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-2 py-1 rounded ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

// ============================================================================
export default function HospitalOrdersPanel() {
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("hospital_token") : null;
  const headers = { Authorization: `Bearer ${token}` };

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/hospital/my-orders`, { headers });
      setOrders(r.data.orders || []);
    } catch (e) {
      toast.error("Could not load your orders");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const stats = useMemo(() => {
    const base = { all: orders.length, open: 0, shipped: 0, delivered: 0 };
    orders.forEach((o) => {
      if (o.status === "shipped") base.shipped += 1;
      else if (o.status === "delivered") base.delivered += 1;
      else if (!["cancelled"].includes(o.status)) base.open += 1;
    });
    return base;
  }, [orders]);

  return (
    <div className="space-y-5" data-testid="hospital-orders-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-emerald-600" /> My Orders
          </h2>
          <p className="text-sm text-slate-500">
            Live status of every quote-cart and direct order your facility has placed with Sterile Mate.
            Tracking numbers, expected delivery, and progress updates show up here automatically.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="hospital-orders-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { key: "all",       label: "Total",     count: stats.all,       icon: Package,      color: "bg-slate-100 text-slate-700" },
          { key: "open",      label: "Open",      count: stats.open,      icon: Clock,        color: "bg-amber-50 text-amber-700" },
          { key: "shipped",   label: "Shipped",   count: stats.shipped,   icon: Truck,        color: "bg-emerald-50 text-emerald-700" },
          { key: "delivered", label: "Delivered", count: stats.delivered, icon: CheckCircle2, color: "bg-slate-50 text-slate-600" },
        ].map((k) => (
          <div key={k.key} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`hospital-orders-kpi-${k.key}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-7 h-7 rounded-md flex items-center justify-center ${k.color}`}><k.icon className="w-3.5 h-3.5" /></div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{k.label}</span>
            </div>
            <div className="text-2xl font-extrabold text-slate-900 leading-none mt-1">{k.count}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2 text-left">Order</th>
                <th className="px-4 py-2 text-left">Items</th>
                <th className="px-4 py-2 text-right">Total</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Tracking</th>
                <th className="px-4 py-2 text-left">ETA</th>
                <th className="px-4 py-2 text-left">Placed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!loading && orders.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-slate-500">
                  <ShoppingCart className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  No orders yet. Submit a quote cart from the Sterile Mate marketplace and it'll show up here.
                </td></tr>
              )}
              {!loading && orders.map((o) => {
                const lineCount = (o.items || []).length;
                const first = (o.items?.[0]?.name) || o.item_name || "—";
                const trackUrl = TRACK_URL(o.carrier, o.tracking_number);
                return (
                  <tr key={o.id} onClick={() => setSelected(o)} className="hover:bg-emerald-50/40 cursor-pointer" data-testid={`hospital-order-row-${o.id}`}>
                    <td className="px-4 py-2.5 align-top">
                      <div className="font-mono text-xs font-bold text-slate-900">{o.id}</div>
                      {o.po_number && <div className="text-[10px] text-slate-400 mt-0.5">PO {o.po_number}</div>}
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="text-xs text-slate-700 truncate max-w-[260px]">{first}</div>
                      {lineCount > 1 && <div className="text-[10px] text-slate-400">+{lineCount - 1} more</div>}
                    </td>
                    <td className="px-4 py-2.5 align-top text-right">
                      <div className="font-bold text-emerald-700 tabular-nums">{fmt$(o.total)}</div>
                      {o.use_hospital_courier ? (
                        <div className="text-[10px] text-slate-400">own freight</div>
                      ) : (o.subtotal > 0 && (
                        <div className="text-[10px] text-slate-400">incl. freight</div>
                      ))}
                    </td>
                    <td className="px-4 py-2.5 align-top"><StatusPill status={o.status} /></td>
                    <td className="px-4 py-2.5 align-top text-xs text-slate-700">
                      {o.tracking_number ? (
                        <>
                          {trackUrl ? (
                            <a href={trackUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="font-mono text-blue-600 hover:underline inline-flex items-center gap-1" data-testid={`hospital-order-track-${o.id}`}>
                              {o.tracking_number} <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="font-mono">{o.tracking_number}</span>
                          )}
                          <div className="text-[10px] text-slate-400">{o.carrier || ""}</div>
                        </>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 align-top text-xs text-slate-600">{fmtDate(o.expected_delivery)}</td>
                    <td className="px-4 py-2.5 align-top text-xs text-slate-500">{fmtAge(o.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <HospitalOrderDetail
          order={selected}
          onClose={() => setSelected(null)}
          onChanged={async (next) => {
            await load();
            if (next === null) setSelected(null);
            else if (next) {
              // Refresh selected from the latest list
              setSelected((cur) => cur ? { ...cur, ...next } : cur);
            }
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
function HospitalOrderDetail({ order, onClose, onChanged }) {
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("hospital_token") : null;
  const hospitalToken = token;
  const headers = { Authorization: `Bearer ${token}` };
  const [draft, setDraft] = useState(order);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [pingMsg, setPingMsg] = useState("");
  const [showPingBox, setShowPingBox] = useState(false);

  useEffect(() => { setDraft(order); }, [order]);

  const canEdit = (draft.status || "new") === "new";
  const canCancel = (draft.status || "new") === "new";
  const trackUrl = TRACK_URL(draft.carrier, draft.tracking_number);

  // Timeline: progress_notes (Sterile Mate updates) + outbound comms (their emails to us) + inbound comms (our own pings)
  const timeline = [
    ...((draft.progress_notes || []).map((n) => ({
      ts: n.ts, kind: n.kind || "note", title: n.kind === "status" ? "Status update" : "Update", body: n.text, from: "Sterile Mate",
    }))),
    ...((draft.communications || []).filter((c) => c.direction === "out").map((c) => ({
      ts: c.ts, kind: "comm", title: `${c.channel === "email" ? "Email" : c.channel} from Sterile Mate`, body: c.summary, from: "Sterile Mate",
    }))),
    ...((draft.communications || []).filter((c) => c.direction === "in").map((c) => ({
      ts: c.ts, kind: "self", title: "You pinged Sterile Mate", body: c.summary, from: "You",
    }))),
  ].sort((a, b) => new Date(b.ts) - new Date(a.ts));

  const saveEdits = async () => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/hospital/my-orders/${order.id}`, {
        shipping_address: draft.shipping_address,
        notes: draft.notes,
        contact_phone: draft.contact_phone,
        use_hospital_courier: !!draft.use_hospital_courier,
        hospital_courier_account: draft.hospital_courier_account || "",
      }, { headers });
      toast.success("Order updated");
      setEditing(false);
      setDraft(r.data.order);
      onChanged?.(r.data.order);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save changes");
    } finally { setSaving(false); }
  };

  const cancelOrder = async () => {
    if (!window.confirm(`Cancel order ${order.id}? This can only be done while status is "new".`)) return;
    try {
      await axios.delete(`${API}/hospital/my-orders/${order.id}`, { headers });
      toast.success(`Order ${order.id} cancelled`);
      onChanged?.(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel order");
    }
  };

  const submitPing = async () => {
    setPinging(true);
    try {
      await axios.post(`${API}/hospital/my-orders/${order.id}/request-update`,
        { message: pingMsg || "Hospital requested a status update." },
        { headers });
      toast.success("Sterile Mate has been notified");
      setShowPingBox(false);
      setPingMsg("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    } finally { setPinging(false); }
  };

  const emailSterileMate = () => {
    const subject = `Question re: order ${order.id}`;
    const body = (
      `Hi Sterile Mate team,\n\nQuestion about my order ${order.id}.\n\n` +
      `Hospital: ${order.hospital_name || ""}\n` +
      `Placed: ${order.created_at || ""}\n` +
      `Current status: ${order.status || "new"}\n\n` +
      `My question:\n\n\n` +
      `Thanks,\n${order.contact_name || ""}`
    );
    window.location.href = `mailto:orders@sterilemate.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} data-testid="hospital-order-detail">
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs font-bold text-slate-900">{draft.id}</span>
              <StatusPill status={draft.status} />
            </div>
            <h2 className="text-lg font-bold text-slate-900">{draft.hospital_name || "—"}</h2>
            <p className="text-xs text-slate-500">
              Placed {fmtAge(draft.created_at)} · {(draft.items || []).length} line{(draft.items || []).length === 1 ? "" : "s"} · <span className="font-bold text-emerald-700">{fmt$(draft.total)}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        {/* Action toolbar */}
        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2 bg-white">
          {canEdit && (
            <Button
              size="sm" variant="outline"
              onClick={() => editing ? saveEdits() : setEditing(true)}
              disabled={saving}
              className="gap-1.5"
              data-testid="hospital-order-edit"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Edit3 className="w-3.5 h-3.5" />}
              {editing ? "Save" : "Edit"}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setShowPingBox((v) => !v)} className="gap-1.5" data-testid="hospital-order-ping">
            <BellRing className="w-3.5 h-3.5" /> Request Update
          </Button>
          <Button size="sm" variant="outline" onClick={emailSterileMate} className="gap-1.5" data-testid="hospital-order-email">
            <Mail className="w-3.5 h-3.5" /> Email Sterile Mate
          </Button>
          {canCancel && (
            <Button
              size="sm" variant="outline"
              onClick={cancelOrder}
              className="gap-1.5 text-rose-600 hover:bg-rose-50 hover:border-rose-300 ml-auto"
              data-testid="hospital-order-cancel"
            >
              <Trash2 className="w-3.5 h-3.5" /> Cancel order
            </Button>
          )}
        </div>

        {showPingBox && (
          <div className="px-5 py-3 border-b border-slate-200 bg-amber-50/60 space-y-2">
            <p className="text-xs font-semibold text-slate-700">Tell Sterile Mate what you need an update on:</p>
            <Textarea
              rows={2}
              placeholder="e.g., Need ETA confirmation for our Q3 OR schedule…"
              value={pingMsg}
              onChange={(e) => setPingMsg(e.target.value)}
              data-testid="hospital-order-ping-msg"
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setShowPingBox(false)}>Cancel</Button>
              <Button size="sm" onClick={submitPing} disabled={pinging} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="hospital-order-ping-submit">
                {pinging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send Request
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Fulfillment snapshot */}
          <section className="rounded-lg border border-slate-200 bg-slate-50 p-4 grid sm:grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Tracking</div>
              {draft.tracking_number ? (
                trackUrl ? (
                  <a href={trackUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                    {draft.tracking_number} <ExternalLink className="w-3 h-3" />
                  </a>
                ) : <span className="font-mono text-sm">{draft.tracking_number}</span>
              ) : <span className="text-slate-400">Awaiting fulfillment</span>}
              <div className="text-xs text-slate-500 mt-0.5">{draft.carrier || ""}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Expected Delivery</div>
              <div className="text-sm font-semibold text-slate-900">{fmtDate(draft.expected_delivery)}</div>
              {draft.shipped_at && <div className="text-xs text-slate-500 mt-0.5">Shipped {fmtTs(draft.shipped_at)}</div>}
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">PO Number</div>
              <div className="text-sm font-semibold text-slate-900 font-mono">{draft.po_number || <span className="text-slate-400 font-sans">—</span>}</div>
            </div>
          </section>

          {/* Fulfillment Timeline — live Amazon-style milestones from Sterile Mate */}
          <section data-testid="my-order-fulfillment-timeline">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5" /> Fulfillment Timeline
            </h3>
            <HospitalBackorderTimeline backorderId={order.id} token={hospitalToken} />
          </section>

          {/* Items */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5" /> Items
            </h3>
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left px-3 py-1.5">Item</th><th className="text-right px-3 py-1.5">Qty</th><th className="text-right px-3 py-1.5">Unit</th><th className="text-right px-3 py-1.5">Line</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(draft.items || []).map((it, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2">{it.name}</td>
                      <td className="px-3 py-2 text-right font-mono">{it.quantity}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{it.unit_price ? fmt$(it.unit_price) : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold">{it.unit_price ? fmt$(it.unit_price * it.quantity) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 text-xs">
                  <tr><td colSpan={3} className="px-3 py-1.5 text-right text-slate-500">Subtotal</td><td className="px-3 py-1.5 text-right font-bold">{fmt$(draft.subtotal)}</td></tr>
                  <tr><td colSpan={3} className="px-3 py-1.5 text-right text-slate-500">Freight {draft.use_hospital_courier ? "(your forwarder)" : "(20%)"}</td><td className="px-3 py-1.5 text-right">{draft.use_hospital_courier ? <span className="text-slate-400 italic">—</span> : <span className="font-semibold">{fmt$(draft.freight_cost)}</span>}</td></tr>
                  <tr className="border-t border-slate-200"><td colSpan={3} className="px-3 py-2 text-right font-bold text-slate-900">Total</td><td className="px-3 py-2 text-right font-extrabold text-emerald-700 text-base">{fmt$(draft.total)}</td></tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* Ship-to (editable when in edit mode) */}
          <section className="grid sm:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> Ship-to {editing && <span className="text-amber-600 font-bold">(editing)</span>}
              </h3>
              {editing ? (
                <Textarea
                  rows={3}
                  value={draft.shipping_address || ""}
                  onChange={(e) => setDraft({ ...draft, shipping_address: e.target.value })}
                  className="text-sm"
                  data-testid="hospital-order-edit-shipto"
                />
              ) : (
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{draft.shipping_address || "—"}</p>
              )}
            </div>
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Submitted by</h3>
              <div className="text-sm text-slate-700 space-y-0.5">
                <div className="font-semibold">{draft.contact_name || "—"}</div>
                <div className="text-slate-500">{draft.contact_email || ""}</div>
                {editing ? (
                  <Input
                    value={draft.contact_phone || ""}
                    onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })}
                    placeholder="Contact phone"
                    className="text-sm mt-1"
                  />
                ) : (
                  <div className="text-slate-500">{draft.contact_phone || ""}</div>
                )}
              </div>
            </div>
          </section>

          {/* Freight toggle (editable when in edit mode and applicable) */}
          {editing && (
            <section className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={!!draft.use_hospital_courier}
                  onChange={(e) => setDraft({ ...draft, use_hospital_courier: e.target.checked })}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300"
                  data-testid="hospital-order-edit-own-freight"
                />
                <div className="flex-1">
                  <span className="font-semibold text-slate-900">We have our own freight forwarder</span>
                  <span className="block text-xs text-slate-500 mt-0.5">Removes the 20% Sterile Mate freight & handling.</span>
                </div>
              </label>
              {draft.use_hospital_courier && (
                <Input
                  className="mt-2"
                  placeholder="Carrier — Acct # — Contact"
                  value={draft.hospital_courier_account || ""}
                  onChange={(e) => setDraft({ ...draft, hospital_courier_account: e.target.value })}
                />
              )}
            </section>
          )}

          {/* Your notes (editable when in edit mode) */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              Your Notes {editing && <span className="text-amber-600 font-bold">(editing)</span>}
            </h3>
            {editing ? (
              <Textarea
                rows={3}
                value={draft.notes || ""}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder="Any special instructions, PO #, etc."
                className="text-sm"
                data-testid="hospital-order-edit-notes"
              />
            ) : (draft.notes ? (
              <div className="bg-amber-50/60 border border-amber-200 rounded p-3 text-sm whitespace-pre-wrap text-slate-700">{draft.notes}</div>
            ) : <p className="text-sm text-slate-400 italic">No customer notes.</p>)}
          </section>

          {/* Timeline */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Order Timeline
            </h3>
            <div className="space-y-2">
              {timeline.length === 0 && <p className="text-sm text-slate-400 italic">No updates yet. Sterile Mate will post status notes here as your order moves through fulfillment.</p>}
              {timeline.map((e, i) => (
                <div key={i} className="border border-slate-200 rounded p-3 bg-white">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${e.kind === "status" ? "bg-violet-50 text-violet-700" : e.kind === "comm" ? "bg-blue-50 text-blue-700" : e.kind === "self" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {e.title}
                    </span>
                    <span className="text-[11px] text-slate-400">{fmtTs(e.ts)}</span>
                  </div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{e.body}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 flex items-start gap-3">
            <Mail className="w-4 h-4 text-emerald-600 mt-0.5" />
            <div className="text-sm text-slate-700">
              Or email <a className="text-emerald-700 font-bold underline" href={`mailto:orders@sterilemate.com?subject=Question%20re%20order%20${draft.id}`}>orders@sterilemate.com</a> directly · call (281) 456-3458 · reference <span className="font-mono font-bold">{draft.id}</span>.
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
